/**
 * Which real on-screen element, if any, the chat widget offers to highlight when its answer was
 * grounded in a given help topic (`BACKLOG.md` B6a's "highlights the specific on-screen element it's
 * talking about"). Deliberately a small, hand-curated, closed mapping rather than something the
 * model itself decides — the model never sees or names a `data-help-id`, so it cannot point the
 * spotlight at an element that does not exist or was renamed. `components/Spotlight.tsx` is the
 * general, reusable mechanism this data feeds; this file is the one place a topic gets an opinion
 * about what to point at, which keeps the mechanism itself topic-agnostic.
 *
 * A topic missing from this map (most of them) simply gets no "Show me" affordance — the chat still
 * answers normally, per `HelpChatWidget.tsx`. Not every topic maps to one obvious element (the
 * `patients-and-scheduling` topic, for instance, is about the *absence* of a screen), so this stays
 * intentionally partial rather than padded out to cover every topic id.
 */

import type { HelpTopic } from '@emr/shared';

export interface SpotlightTarget {
  /** Matches a `data-help-id` attribute somewhere in the DOM. */
  readonly targetId: string;
  /** Read to assistive tech and shown as the spotlight's caption — names the control, not the
   * glyph, matching `invariants.md`'s `HelpLink` accessible-name convention. */
  readonly label: string;
}

export const TOPIC_SPOTLIGHT_TARGETS: Partial<Record<HelpTopic['id'], SpotlightTarget>> = {
  'your-access': { targetId: 'nav-access', label: 'The "Your access" navigation link' },
  'signing-in': { targetId: 'sign-out-button', label: 'The Sign out button' },
  'plans-and-upgrades': { targetId: 'nav-analytics', label: 'The "Analytics" navigation link' },
  'audit-log': { targetId: 'nav-audit', label: 'The "Audit log" navigation link' },
  'secure-messaging': { targetId: 'nav-inbox', label: 'The "Inbox" navigation link' },
};

export function spotlightTargetFor(topicId: string): SpotlightTarget | null {
  return TOPIC_SPOTLIGHT_TARGETS[topicId] ?? null;
}
